import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { digitalEmployees } from '../data';
import { DigitalEmployee } from '../types';

interface CollaborationNode {
  id: string;
  employeeId: string;
  x: number;
  y: number;
  role: string;
}

interface CollaborationLink {
  source: string;
  target: string;
  label: string;
}

const CollaborationPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedEmployees, setSelectedEmployees] = useState<DigitalEmployee[]>([]);
  const [availableEmployees, setAvailableEmployees] = useState<DigitalEmployee[]>(digitalEmployees);
  const [task, setTask] = useState('');
  const [nodes, setNodes] = useState<CollaborationNode[]>([]);
  const [links, setLinks] = useState<CollaborationLink[]>([]);
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [linkStart, setLinkStart] = useState<string | null>(null);
  const [linkTarget, setLinkTarget] = useState<string | null>(null);
  const [linkLabel, setLinkLabel] = useState('');
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [tempLink, setTempLink] = useState<{x1: number, y1: number, x2: number, y2: number} | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [nodeRole, setNodeRole] = useState('');

  // Handle employee selection
  const handleSelectEmployee = (employee: DigitalEmployee) => {
    setSelectedEmployees([...selectedEmployees, employee]);
    setAvailableEmployees(availableEmployees.filter(e => e.id !== employee.id));

    // Add a node for the selected employee
    const newNode: CollaborationNode = {
      id: `node-${Date.now()}`,
      employeeId: employee.id,
      x: Math.random() * 600 + 100,
      y: Math.random() * 300 + 100,
      role: 'Contributor'
    };
    setNodes([...nodes, newNode]);
  };

  // Handle employee removal
  const handleRemoveEmployee = (employeeId: string) => {
    const employee = digitalEmployees.find(e => e.id === employeeId);
    if (employee) {
      setSelectedEmployees(selectedEmployees.filter(e => e.id !== employeeId));
      setAvailableEmployees([...availableEmployees, employee]);

      // Remove nodes and links associated with this employee
      const nodesToRemove = nodes.filter(node => node.employeeId === employeeId);
      const nodeIdsToRemove = nodesToRemove.map(node => node.id);

      setNodes(nodes.filter(node => node.employeeId !== employeeId));
      setLinks(links.filter(link =>
        !nodeIdsToRemove.includes(link.source) && !nodeIdsToRemove.includes(link.target)
      ));
    }
  };

  // Handle node dragging
  const handleNodeMouseDown = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    setActiveNode(nodeId);
  };

  // Handle mouse move for dragging nodes
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (activeNode && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        setNodes(nodes.map(node =>
          node.id === activeNode ? { ...node, x, y } : node
        ));

        // Update temp link if creating a link
        if (isCreatingLink && linkStart) {
          const startNode = nodes.find(node => node.id === linkStart);
          if (startNode) {
            setTempLink({
              x1: startNode.x,
              y1: startNode.y,
              x2: x,
              y2: y
            });
          }
        }
      }

      setMousePos({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      if (activeNode) {
        setActiveNode(null);
      }

      if (isCreatingLink) {
        // Check if mouse is over a node
        const targetNode = nodes.find(node => {
          const dx = mousePos.x - node.x;
          const dy = mousePos.y - node.y;
          return Math.sqrt(dx * dx + dy * dy) < 40 && node.id !== linkStart;
        });

        if (targetNode && linkStart) {
          setLinkTarget(targetNode.id);
          setShowLinkDialog(true);
          setTempLink(null);
        } else {
          setIsCreatingLink(false);
          setLinkStart(null);
          setTempLink(null);
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [activeNode, nodes, isCreatingLink, linkStart, mousePos]);

  // Start creating a link
  const handleStartLink = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    setIsCreatingLink(true);
    setLinkStart(nodeId);

    const startNode = nodes.find(node => node.id === nodeId);
    if (startNode && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setTempLink({
        x1: startNode.x,
        y1: startNode.y,
        x2: e.clientX - rect.left,
        y2: e.clientY - rect.top
      });
    }
  };

  // Create a link between nodes
  const handleCreateLink = () => {
    if (linkStart && linkTarget) {
      const newLink: CollaborationLink = {
        source: linkStart,
        target: linkTarget,
        label: linkLabel
      };

      setLinks([...links, newLink]);
      setLinkLabel('');
      setShowLinkDialog(false);
      setIsCreatingLink(false);
      setLinkStart(null);
      setLinkTarget(null);
    }
  };

  // Cancel link creation
  const handleCancelLink = () => {
    setShowLinkDialog(false);
    setIsCreatingLink(false);
    setLinkStart(null);
    setLinkTarget(null);
    setLinkLabel('');
  };

  // Edit node role
  const handleEditNode = (nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setEditingNodeId(nodeId);
      setNodeRole(node.role);
    }
  };

  // Save node role
  const handleSaveNodeRole = () => {
    if (editingNodeId) {
      setNodes(nodes.map(node =>
        node.id === editingNodeId ? { ...node, role: nodeRole } : node
      ));
      setEditingNodeId(null);
    }
  };

  // Delete a link
  const handleDeleteLink = (source: string, target: string) => {
    setLinks(links.filter(link =>
      !(link.source === source && link.target === target)
    ));
  };

  // Submit the collaboration task
  const handleSubmitTask = () => {
    if (task.trim() === '') {
      alert('Please enter a task description');
      return;
    }

    if (selectedEmployees.length === 0) {
      alert('Please select at least one AI Employee');
      return;
    }

    // In a real application, this would send the task, nodes, and links to a backend
    alert(`Task submitted: ${task}\nCollaborating AI Employees: ${selectedEmployees.map(e => e.name).join(', ')}`);

    // Navigate back to home page or to a task status page
    navigate('/');
  };

  // Get employee by ID
  const getEmployeeById = (id: string) => {
    return digitalEmployees.find(e => e.id === id);
  };

  // Get node by ID
  const getNodeById = (id: string) => {
    return nodes.find(n => n.id === id);
  };

  return (
    <div className="collaboration-page">
      <h1>AI Employee Collaboration</h1>

      <div className="collaboration-container">
        <div className="collaboration-sidebar">
          <div className="task-section">
            <h2>Task Description</h2>
            <textarea
              value={task}
              onChange={(e) => setTask(e.target.value)}
              placeholder="Describe the task you want the AI Employees to collaborate on..."
              rows={5}
              className="task-input"
            />
          </div>

          <div className="employees-section">
            <h2>Available AI Employees</h2>
            <div className="available-employees">
              {availableEmployees.map(employee => (
                <div key={employee.id} className="employee-selection-card">
                  <img src={employee.avatar} alt={employee.name} className="employee-avatar" />
                  <div className="employee-info">
                    <h3>{employee.name}</h3>
                    <p>{employee.skills.slice(0, 2).join(', ')}</p>
                  </div>
                  <button
                    className="add-employee-button"
                    onClick={() => handleSelectEmployee(employee)}
                  >
                    Add
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="selected-employees-section">
            <h2>Selected AI Employees</h2>
            <div className="selected-employees">
              {selectedEmployees.map(employee => (
                <div key={employee.id} className="employee-selection-card">
                  <img src={employee.avatar} alt={employee.name} className="employee-avatar" />
                  <div className="employee-info">
                    <h3>{employee.name}</h3>
                    <p>{employee.skills.slice(0, 2).join(', ')}</p>
                  </div>
                  <button
                    className="remove-employee-button"
                    onClick={() => handleRemoveEmployee(employee.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            className="submit-task-button"
            onClick={handleSubmitTask}
            disabled={task.trim() === '' || selectedEmployees.length === 0}
          >
            Submit Collaboration Task
          </button>
        </div>

        <div className="collaboration-canvas-container">
          <h2>Collaboration Workflow</h2>
          <p className="canvas-instructions">
            Drag AI Employees to position them. Click the "Link" button on an employee to create connections.
            Double-click an employee to edit their role in the collaboration.
          </p>

          <div
            className="collaboration-canvas"
            ref={canvasRef}
            onClick={() => {
              setActiveNode(null);
              if (isCreatingLink) {
                setIsCreatingLink(false);
                setLinkStart(null);
                setTempLink(null);
              }
            }}
          >
            {/* Draw links between nodes */}
            {links.map((link, index) => {
              const sourceNode = getNodeById(link.source);
              const targetNode = getNodeById(link.target);

              if (sourceNode && targetNode) {
                // Calculate the line coordinates
                const x1 = sourceNode.x;
                const y1 = sourceNode.y;
                const x2 = targetNode.x;
                const y2 = targetNode.y;

                // Calculate the midpoint for the label
                const midX = (x1 + x2) / 2;
                const midY = (y1 + y2) / 2;

                return (
                  <div key={`link-${index}`} className="node-link-container">
                    <svg className="node-link">
                      <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke="#4a6bff"
                        strokeWidth="2"
                        markerEnd="url(#arrowhead)"
                      />
                      <defs>
                        <marker
                          id="arrowhead"
                          markerWidth="10"
                          markerHeight="7"
                          refX="9"
                          refY="3.5"
                          orient="auto"
                        >
                          <polygon points="0 0, 10 3.5, 0 7" fill="#4a6bff" />
                        </marker>
                      </defs>
                    </svg>
                    <div
                      className="link-label"
                      style={{ left: midX, top: midY }}
                    >
                      {link.label}
                      <button
                        className="delete-link-button"
                        onClick={() => handleDeleteLink(link.source, link.target)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              }
              return null;
            })}

            {/* Draw temporary link while creating */}
            {tempLink && (
              <svg className="temp-link">
                <line
                  x1={tempLink.x1}
                  y1={tempLink.y1}
                  x2={tempLink.x2}
                  y2={tempLink.y2}
                  stroke="#4a6bff"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />
              </svg>
            )}

            {/* Draw nodes for each employee */}
            {nodes.map(node => {
              const employee = getEmployeeById(node.employeeId);

              if (employee) {
                return (
                  <div
                    key={node.id}
                    className={`collaboration-node ${activeNode === node.id ? 'active' : ''}`}
                    style={{ left: node.x, top: node.y }}
                    onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                  >
                    <img
                      src={employee.avatar}
                      alt={employee.name}
                      className="node-avatar"
                    />
                    <div className="node-info">
                      <h4>{employee.name}</h4>
                      <p className="node-role">{node.role}</p>
                    </div>
                    <div className="node-actions">
                      <button
                        className="link-node-button"
                        onClick={(e) => handleStartLink(e, node.id)}
                      >
                        Link
                      </button>
                      <button
                        className="edit-node-button"
                        onClick={() => handleEditNode(node.id)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      </div>

      {/* Link creation dialog */}
      {showLinkDialog && (
        <div className="link-dialog-overlay">
          <div className="link-dialog">
            <h3>Define Interaction</h3>
            <p>Describe how these AI Employees will interact:</p>
            <input
              type="text"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              placeholder="e.g., Provides data to, Reviews work of, etc."
              className="link-label-input"
            />
            <div className="link-dialog-buttons">
              <button
                className="cancel-link-button"
                onClick={handleCancelLink}
              >
                Cancel
              </button>
              <button
                className="create-link-button"
                onClick={handleCreateLink}
                disabled={linkLabel.trim() === ''}
              >
                Create Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Node role editing dialog */}
      {editingNodeId && (
        <div className="role-dialog-overlay">
          <div className="role-dialog">
            <h3>Edit Role</h3>
            <p>Define this AI Employee's role in the collaboration:</p>
            <input
              type="text"
              value={nodeRole}
              onChange={(e) => setNodeRole(e.target.value)}
              placeholder="e.g., Project Manager, Researcher, Writer, etc."
              className="role-input"
            />
            <div className="role-dialog-buttons">
              <button
                className="cancel-role-button"
                onClick={() => setEditingNodeId(null)}
              >
                Cancel
              </button>
              <button
                className="save-role-button"
                onClick={handleSaveNodeRole}
                disabled={nodeRole.trim() === ''}
              >
                Save Role
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaborationPage;
